import type { MutableRefObject } from "react";
import { getLoggerRef } from "../../integrations/logger/logger-bridge";
import { uploadFullVideoToS3 } from "./utils";

export interface CallRecordingDeps {
  mediaRecorderRef: MutableRefObject<any>;
  audioMediaRecorderRef: MutableRefObject<any>;
  outgoingMediaRecorderRef: MutableRefObject<any>;
  incomingMediaRecorderRef: MutableRefObject<any>;
  incomingAudioChunks: MutableRefObject<any[]>;
  outgoingAudioChunks: MutableRefObject<any[]>;
  recordingCountRef: MutableRefObject<number>;
  pendingEndReasonRef: MutableRefObject<string | null>;
  videoEndReasonResolveRef: MutableRefObject<(() => void) | null>;
  videoEndReasonTimeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  screenStreamRef: MutableRefObject<MediaStream | null>;
  webcamStreamRef: MutableRefObject<MediaStream | null>;
  webcamVideoRef: MutableRefObject<HTMLVideoElement | null>;
  // Refs — read via .current so the long-lived MediaRecorder callbacks
  // (onstop/ondataavailable, attached once at recording start) stay fresh.
  callDetailsRef: MutableRefObject<any>;
  onDisconnectRef: MutableRefObject<any>;
  clearCallStateOnErrorRef: MutableRefObject<() => void>;
  // Plain — stopRecording's own catch (no-mediaRecorder branch) runs synchronously
  // as part of a fresh-per-render call chain (handleClose), unlike
  // executeEndReasonFromRecording above which needs the ref for callback freshness.
  clearCallStateOnError: () => void;
  setLoading: (value: boolean) => void;
  setDataSavingProgress: (value: number) => void;
  // Plain values — snapshotted at whichever render's factory call actually gets
  // invoked (matching the original inline functions' closure semantics exactly):
  // frozen-at-recording-start inside the MediaRecorder callbacks, and current
  // when read synchronously (e.g. stopRecording's no-mediaRecorder branch).
  tenantProfile: any;
  credentials: any;
  userInfo: any;
  callDetails: any;
  onDisconnect: any;
}

/**
 * Screen/webcam video recording + the audio blobs assembled from the outgoing
 * (mic) and incoming (assistant) MediaRecorders used for the WebRTC call.
 */
export function createCallRecording(deps: CallRecordingDeps) {
  const {
    mediaRecorderRef,
    audioMediaRecorderRef,
    outgoingMediaRecorderRef,
    incomingMediaRecorderRef,
    incomingAudioChunks,
    outgoingAudioChunks,
    recordingCountRef,
    pendingEndReasonRef,
    videoEndReasonResolveRef,
    videoEndReasonTimeoutRef,
    screenStreamRef,
    webcamStreamRef,
    webcamVideoRef,
    callDetailsRef,
    onDisconnectRef,
    clearCallStateOnErrorRef,
    clearCallStateOnError,
    setLoading,
    setDataSavingProgress,
    tenantProfile,
    credentials,
    userInfo,
    callDetails,
    onDisconnect,
  } = deps;

  const getAudioRecordings = () => {
    let user_audio: any = null;
    let assistant_audio: any = null;
    // outgoingMediaRecorder
    if (
      outgoingMediaRecorderRef.current
    ) {
      outgoingMediaRecorderRef.current.stop();
      const audioBlob = new Blob(outgoingAudioChunks.current, {
        type: "audio/webm;codecs=opus",
      });
      user_audio = audioBlob;
    }

    // incomingMediaRecorder
    if (
      incomingMediaRecorderRef.current &&
      incomingMediaRecorderRef.current.state !== "inactive"
    ) {
      incomingMediaRecorderRef.current.stop();
      const audioBlob = new Blob(incomingAudioChunks.current, {
        type: "audio/webm;codecs=opus",
      });
      assistant_audio = audioBlob;
    }

    return { user_audio, assistant_audio };
  };

  /** Shared logic for endReason disconnect from MediaRecorder onstop and 15s timeout fallback. */
  const executeEndReasonFromRecording = async (
    endReasonVal: string,
    resolve: () => void,
    errorLabel = "endReason"
  ) => {
    try {
      const callId = callDetailsRef.current?.id;
      if (endReasonVal && callId) {
        setLoading(true);
        const { user_audio, assistant_audio } = getAudioRecordings();
        await onDisconnectRef.current({ user_audio, assistant_audio, endReason: endReasonVal });
      } else if (endReasonVal) {
        clearCallStateOnErrorRef.current();
      }
    } catch (err) {
      getLoggerRef()?.error(`Error ending call with ${errorLabel}`, err as Error, { callId: callDetailsRef.current?.id });
      console.error(`Error ending call with ${errorLabel}:`, err);
      clearCallStateOnErrorRef.current();
    } finally {
      resolve();
    }
  };

  const startRecording = async () => {
    try {
      // Capture screen stream
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          // @ts-ignore
          displaySurface: "browser",
        },
        // @ts-ignore
        preferCurrentTab: true,
        // @ts-ignore
        selfBrowserSurface: "include",
        audio: false,
      });
      screenStreamRef.current = screenStream;

      // Capture webcam stream
      const webcamStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      webcamStreamRef.current = webcamStream;

      // Attach webcam stream to PiP video element
      if (webcamVideoRef.current) {
        webcamVideoRef.current.srcObject = webcamStream;
        await webcamVideoRef.current.play();
      }
      // @ts-ignore
      if (!window.stream) {
        // @ts-ignore
        window.stream = [];
      }
      // @ts-ignore
      window.stream.push(screenStream);
      // @ts-ignore
      window.stream.push(webcamStream);

      // Combine both screen (with system audio) and microphone audio
      const combinedStream = new MediaStream([
        ...screenStream.getTracks(),
        ...webcamStream.getTracks(),
      ]);

      // Start recording with MediaRecorder
      let options = { mimeType: 'video/webm;codecs=vp9,opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'video/webm;codecs=vp8,opus' };
      }
      const mediaRecorder = new MediaRecorder(combinedStream, options);
      mediaRecorderRef.current = mediaRecorder;

      /**
       * Single handler for endReason flow. Per MediaRecorder spec, onstop fires exactly once
       * after ondataavailable when stop() is called. Uses refs (onDisconnectRef, callDetailsRef)
       * to avoid stale closures since this handler is set at recording start.
       */
      mediaRecorder.onstop = () => {
        const resolve = videoEndReasonResolveRef.current;
        if (!resolve) return;
        const endReason = pendingEndReasonRef.current;
        videoEndReasonResolveRef.current = null;
        pendingEndReasonRef.current = null;
        if (videoEndReasonTimeoutRef.current) {
          clearTimeout(videoEndReasonTimeoutRef.current);
          videoEndReasonTimeoutRef.current = null;
        }
        if (endReason) {
          executeEndReasonFromRecording(endReason, resolve, "endReason");
        } else {
          resolve();
        }
      };

      mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          const blob = new Blob([event.data], { type: "video/webm" });
          recordingCountRef.current++;

          try {
            const details = callDetailsRef.current;
            if (details?.id) {
              if (pendingEndReasonRef.current) {
                return;
              }
              setLoading(true);
              const { user_audio, assistant_audio } = getAudioRecordings();
              if (details?.isVideoRecording) {
                setDataSavingProgress(10)
                await uploadFullVideoToS3(
                  blob,
                  "session-raw-video.webm",
                  tenantProfile?.assets_bucket,
                  userInfo?.["sub"],
                  details?.id as string,
                  credentials
                );
                setDataSavingProgress(40)
                await uploadFullVideoToS3(
                  user_audio,
                  "user_audio.webm",
                  tenantProfile?.assets_bucket,
                  userInfo?.["sub"],
                  details?.id as string,
                  credentials
                );
                setDataSavingProgress(60)
                await uploadFullVideoToS3(
                  assistant_audio,
                  "assistant_audio.webm",
                  tenantProfile?.assets_bucket,
                  userInfo?.["sub"],
                  details?.id as string,
                  credentials
                );
              }
              setDataSavingProgress(80)
              await onDisconnectRef.current({ user_audio, assistant_audio });
              setDataSavingProgress(100)
            }
          } catch (err) {
            getLoggerRef()?.error("Error uploading file", err as Error, { callId: callDetailsRef.current?.id });
            console.error("Error uploading file:", err);
            setLoading(false);
          }
        }
      };

      mediaRecorder.start();
    } catch (err) {
      getLoggerRef()?.error("Error starting recording", err as Error, { callId: callDetails?.id });
      console.error("Error starting recording:", err);
    }
  };

  const stopRecording = async (endReason?: string): Promise<void> => {
    if (mediaRecorderRef.current) {
      if (endReason) {
        pendingEndReasonRef.current = endReason;
        const promise = new Promise<void>((resolve) => {
          videoEndReasonResolveRef.current = resolve;
          videoEndReasonTimeoutRef.current = setTimeout(() => {
            const resolveFn = videoEndReasonResolveRef.current;
            if (!resolveFn) return;
            const endReasonVal = pendingEndReasonRef.current;
            videoEndReasonResolveRef.current = null;
            pendingEndReasonRef.current = null;
            videoEndReasonTimeoutRef.current = null;
            if (endReasonVal) {
              executeEndReasonFromRecording(endReasonVal, resolveFn, "endReason (timeout fallback)");
            } else {
              resolveFn();
            }
          }, 15000);
        });
        mediaRecorderRef.current.stop();
        await promise;
      } else {
        mediaRecorderRef.current.stop();
      }
    } else {
      try {
        setLoading(true);
        const { user_audio, assistant_audio } = getAudioRecordings();
        if (endReason) {
          setDataSavingProgress(70);
          await onDisconnect({ user_audio, assistant_audio, endReason });
        } else {
          // No media recorder (recording never started) - upload audio and disconnect
          setDataSavingProgress(10);
          await uploadFullVideoToS3(
            user_audio,
            "user_audio.webm",
            tenantProfile?.assets_bucket,
            userInfo?.["sub"],
            callDetails?.id as string,
            credentials
          );
          setDataSavingProgress(40);
          await uploadFullVideoToS3(
            assistant_audio,
            "assistant_audio.webm",
            tenantProfile?.assets_bucket,
            userInfo?.["sub"],
            callDetails?.id as string,
            credentials
          );
          setDataSavingProgress(70);
          await onDisconnect({ user_audio, assistant_audio });
          setDataSavingProgress(100);
        }
      } catch (err) {
        getLoggerRef()?.error("Error in stopRecording (no media recorder)", err as Error, { callId: callDetails?.id });
        console.error("Error in stopRecording (no media recorder):", err);
        clearCallStateOnError();
      }
    }
  };

  const restartAudioRecording = () => {
    if (
      audioMediaRecorderRef.current &&
      audioMediaRecorderRef.current.state !== "recording" &&
      callDetails?.isVideoRecording
    ) {
      audioMediaRecorderRef.current.start();
    }
  };

  return { startRecording, stopRecording, getAudioRecordings, restartAudioRecording };
}
